// The backend may return `movingResponses` as an object or as a single-item array (both were
// seen in examples) — normalize to a single { sections, states } object either way.
// Every solved train as an array (the backend may ship one object or an array). Each entry keeps
// its own sections/states/envelope plus any `combinations[]` that reference that train.
export function normalizeMovingResponses(data) {
  const mr = data?.movingResponses
  if (!mr) return []
  return Array.isArray(mr) ? mr.filter(Boolean) : [mr]
}

const RESPONSE_TO_DIAGRAM_KEY = { axial: 'N', shear: 'V', moment: 'M' }

// The backend's raw `shear` sign is opposite the convention the frontend already uses for the
// static solver's reconstructed N/V/M (see diagrams.js) — flipping it here means the same display
// logic in StructureCanvas.js works identically for both static and moving-load diagrams, instead
// of drawing it mirrored for moving loads. `moment` and `axial` already arrive in that convention
// (confirmed against the API: positive moment = sagging, drawn downward same as the static case)
// and must NOT be flipped, despite this having been the case previously — that was the source of a
// bug where the moment envelope was drawn mirrored versus the static diagrams.
const FLIPPED_RESPONSE_KEYS = new Set(['shear'])
function readResponseValue(values, index, responseKey) {
  const v = values[index]
  return FLIPPED_RESPONSE_KEYS.has(responseKey) ? -v : v
}

function groupSectionsByElement(sections) {
  const byElement = {}
  sections.forEach((s, index) => {
    if (!byElement[s.element]) byElement[s.element] = []
    byElement[s.element].push({ index, x: s.localPosition })
  })
  Object.values(byElement).forEach((list) => list.sort((a, b) => a.x - b.x))
  return byElement
}

// Builds a per-element {xs, N, V, M} diagram for a single state (one train reference position),
// in the same shape `computeAllDiagrams` produces for the static solver, so the existing ribbon
// rendering on the canvas can be reused as-is.
export function buildStateDiagram(movingResponse, referencePosition) {
  if (!movingResponse) return null
  const state = movingResponse.states.find((s) => s.referencePosition === referencePosition)
  if (!state) return null
  const byElement = groupSectionsByElement(movingResponse.sections)

  const diagramsByElementId = {}
  const maxAbs = { N: 0, V: 0, M: 0 }

  Object.entries(byElement).forEach(([elementId, list]) => {
    const diagram = { xs: list.map((e) => e.x) }
    Object.entries(RESPONSE_TO_DIAGRAM_KEY).forEach(([responseKey, diagramKey]) => {
      const values = state.values[responseKey]
      if (!values) return
      diagram[diagramKey] = list.map((e) => readResponseValue(values, e.index, responseKey))
      diagram[diagramKey].forEach((v) => {
        maxAbs[diagramKey] = Math.max(maxAbs[diagramKey], Math.abs(v) || 0)
      })
    })
    diagramsByElementId[elementId] = diagram
  })

  return { diagramsByElementId, maxAbs, axles: state.axles, referencePosition: state.referencePosition }
}

// Aggregates every state's values per section into a max/min envelope per element — the backend
// only returns the diagram for one train position at a time, so the full-beam N/V/M envelope
// (max and min at each cross section, across every train position) is built here client-side.
// `xsByKey` stores the same `xs` under each diagramKey so it can be merged uniformly with
// `buildNativeEnvelopeDiagram`'s per-key result (see `mergeEnvelopeDiagrams`).
export function buildEnvelopeDiagram(movingResponse) {
  if (!movingResponse) return null
  const byElement = groupSectionsByElement(movingResponse.sections)

  const diagramsByElementId = {}
  const maxAbs = { N: 0, V: 0, M: 0 }

  Object.entries(byElement).forEach(([elementId, list]) => {
    const xs = list.map((e) => e.x)
    const diagram = { xs, xsByKey: {}, max: {}, min: {} }
    Object.entries(RESPONSE_TO_DIAGRAM_KEY).forEach(([responseKey, diagramKey]) => {
      const maxValues = []
      const minValues = []
      list.forEach((e) => {
        let maxV = -Infinity
        let minV = Infinity
        movingResponse.states.forEach((state) => {
          const raw = state.values[responseKey]?.[e.index]
          if (raw === undefined || raw === null || isNaN(raw)) return
          const v = readResponseValue(state.values[responseKey], e.index, responseKey)
          if (v > maxV) maxV = v
          if (v < minV) minV = v
        })
        maxValues.push(maxV === -Infinity ? 0 : maxV)
        minValues.push(minV === Infinity ? 0 : minV)
      })
      diagram.xsByKey[diagramKey] = xs
      diagram.max[diagramKey] = maxValues
      diagram.min[diagramKey] = minValues
      maxValues.concat(minValues).forEach((v) => {
        maxAbs[diagramKey] = Math.max(maxAbs[diagramKey], Math.abs(v) || 0)
      })
    })
    diagramsByElementId[elementId] = diagram
  })

  return { diagramsByElementId, maxAbs }
}

// Combines the client-aggregated envelope with the backend's native one, per N/V/M component
// independently — the backend may only have shipped the native `.display` field for some
// components (e.g. just shear) so far, and each component's xs can come from a different source
// (`sections[]` grouping vs. the native envelope's own position series).
export function mergeEnvelopeDiagrams(computed, native) {
  if (!native) return computed
  if (!computed) return native

  const diagramsByElementId = {}
  const maxAbs = { ...computed.maxAbs }

  const elementIds = new Set([...Object.keys(computed.diagramsByElementId), ...Object.keys(native.diagramsByElementId)])
  elementIds.forEach((elementId) => {
    const computedEl = computed.diagramsByElementId[elementId]
    const nativeEl = native.diagramsByElementId[elementId]
    const merged = { xsByKey: {}, max: {}, min: {} }
    ;['N', 'V', 'M'].forEach((diagramKey) => {
      const source = nativeEl?.max?.[diagramKey] ? nativeEl : computedEl
      if (!source) return
      merged.xsByKey[diagramKey] = source.xsByKey ? source.xsByKey[diagramKey] : source.xs
      merged.max[diagramKey] = source.max[diagramKey]
      merged.min[diagramKey] = source.min[diagramKey]
    })
    diagramsByElementId[elementId] = merged
  })

  Object.keys(native.maxAbs).forEach((diagramKey) => {
    if (native.maxAbs[diagramKey] > 0) maxAbs[diagramKey] = native.maxAbs[diagramKey]
  })

  return { diagramsByElementId, maxAbs }
}

function valueAtPosition(points, target, key) {
  if (points.length === 0) return 0
  if (target <= points[0].position) return points[0][key]
  if (target >= points[points.length - 1].position) return points[points.length - 1][key]
  for (let i = 1; i < points.length; i += 1) {
    if (target <= points[i].position) {
      const span = points[i].position - points[i - 1].position || 1
      const t = (target - points[i - 1].position) / span
      return points[i - 1][key] + (points[i][key] - points[i - 1][key]) * t
    }
  }
  return points[points.length - 1][key]
}

// Builds a per-element {xs, max, min} envelope diagram straight from the backend's native
// `movingResponses[].envelope[responseKey].values` (position/max/min triples) — the actual, full
// min/max range for the combination, already in the sign convention the frontend displays (see
// HANDOFF.md "envoltória de cortante"). Falls back to the older `.display.values` field (zero-clipped
// for a conventional filled diagram, and in the backend's raw sign needing the `FLIPPED_RESPONSE_KEYS`
// flip) when a component hasn't shipped `.values` yet — which is why the moment envelope, which never
// had a native `.values`/`.display` field, keeps falling back to the client-aggregated
// `buildEnvelopeDiagram` untouched. Do NOT read `.display.values` when `.values` is present: `.display`
// forces one bound to zero (`displayMax = max(0, -min)`, `displayMin = min(0, -max)`), which loses the
// real range whenever min and max share a sign (e.g. a support's shear envelope that never crosses zero).
export function buildNativeEnvelopeDiagram(envelope, pathLayout) {
  if (!envelope || pathLayout.length === 0) return null

  const diagramsByElementId = {}
  const maxAbs = { N: 0, V: 0, M: 0 }
  let hasAnyData = false

  Object.entries(RESPONSE_TO_DIAGRAM_KEY).forEach(([responseKey, diagramKey]) => {
    const rawPoints = envelope[responseKey]?.values
    const usingRaw = Boolean(rawPoints && rawPoints.length > 0)
    const points = usingRaw ? rawPoints : envelope[responseKey]?.display?.values
    if (!points || points.length === 0) return
    hasAnyData = true
    // `.values` already matches the frontend's display convention (no flip needed). Only the legacy
    // `.display` fallback carries the backend's raw sign and still needs the flip.
    const flip = !usingRaw && FLIPPED_RESPONSE_KEYS.has(responseKey)
    const sorted = [...points].sort((a, b) => a.position - b.position)

    pathLayout.forEach((seg) => {
      const segEnd = seg.start + seg.length
      const inner = sorted.filter((p) => p.position > seg.start && p.position < segEnd)
      const boundary = [
        {
          position: seg.start,
          max: valueAtPosition(sorted, seg.start, 'max'),
          min: valueAtPosition(sorted, seg.start, 'min'),
        },
        ...inner,
        {
          position: segEnd,
          max: valueAtPosition(sorted, segEnd, 'max'),
          min: valueAtPosition(sorted, segEnd, 'min'),
        },
      ]

      let xs = boundary.map((p) => p.position - seg.start)
      let maxValues = boundary.map((p) => p.max)
      let minValues = boundary.map((p) => p.min)
      if (seg.reverse) {
        xs = xs.map((x) => seg.length - x).reverse()
        maxValues = maxValues.slice().reverse()
        minValues = minValues.slice().reverse()
      }

      if (!diagramsByElementId[seg.element.id]) {
        diagramsByElementId[seg.element.id] = { xsByKey: {}, max: {}, min: {} }
      }
      diagramsByElementId[seg.element.id].xsByKey[diagramKey] = xs
      diagramsByElementId[seg.element.id].max[diagramKey] = flip ? minValues.map((v) => -v) : maxValues
      diagramsByElementId[seg.element.id].min[diagramKey] = flip ? maxValues.map((v) => -v) : minValues
      maxValues.concat(minValues).forEach((v) => {
        maxAbs[diagramKey] = Math.max(maxAbs[diagramKey], Math.abs(v) || 0)
      })
    })
  })

  return hasAnyData ? { diagramsByElementId, maxAbs } : null
}

// Cumulative-length layout of the path (each segment's element, endpoints, and start/length
// along the path), used to place a train axle's path-cumulative `position` in world space.
export function buildPathLayout(path, elementsById, nodesById) {
  let cursor = 0
  const layout = []
  ;(path?.segments || []).forEach((seg) => {
    const el = elementsById[seg.element]
    const nodeI = el && nodesById[el.nodeI]
    const nodeJ = el && nodesById[el.nodeJ]
    if (!el || !nodeI || !nodeJ) return
    const length = Math.hypot(nodeJ.x - nodeI.x, nodeJ.y - nodeI.y) || 1
    layout.push({ element: el, nodeI, nodeJ, reverse: !!seg.reverse, start: cursor, length })
    cursor += length
  })
  return layout
}

export function pathPositionToModelPoint(layout, position) {
  for (const seg of layout) {
    if (position >= seg.start - 1e-6 && position <= seg.start + seg.length + 1e-6) {
      const localX = seg.reverse ? seg.length - (position - seg.start) : position - seg.start
      const t = seg.length === 0 ? 0 : localX / seg.length
      return {
        x: seg.nodeI.x + (seg.nodeJ.x - seg.nodeI.x) * t,
        y: seg.nodeI.y + (seg.nodeJ.y - seg.nodeI.y) * t,
      }
    }
  }
  return null
}
